import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NoticeGroup,
} from '../api/employeeNotificationsApi';
import { employeeNotificationKeys } from '../queryKeys';

/**
 * The bell's inbox. Polled every minute as a safety net; the WebSocket signal
 * (see `NotificationBell`) refetches it the moment something new is written.
 */
export function useMyNotifications(employeeId: number | null | undefined, group?: NoticeGroup) {
  const valid = Number.isInteger(employeeId) && (employeeId as number) > 0;
  return useQuery({
    queryKey: employeeNotificationKeys.mine(valid ? (employeeId as number) : 0, group),
    queryFn: () => fetchMyNotifications(group),
    enabled: valid,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: employeeNotificationKeys.all }),
  });
}

export function useMarkAllNotificationsRead(group?: NoticeGroup) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(group),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: employeeNotificationKeys.all }),
  });
}
